/**
 * PROJECTION d'une équipe (mode dédié) : à partir d'une sortie Monte Carlo
 * (probas par tour + `slotDistribution` = qui occupe chaque créneau du bracket),
 * on dérive, pour une équipe donnée :
 *  - sa proba d'atteindre chaque tour (parcours probable) ;
 *  - ses ADVERSAIRES POSSIBLES à chaque tour, avec probabilité.
 * Fonctions PURES → testables hors DOM.
 */
import type { SimulationOutput } from "../engine/montecarlo.ts";
import type { TeamCode } from "../engine/types.ts";
import bracketJson from "../data/bracket.json";

const NODES = bracketJson.nodes as unknown as Record<string, { round: string }>;

/** Tours du tableau final, du plus tôt au plus tard (3e place exclue du parcours). */
export const ROUNDS = [
  { key: "r32", label: "16es de finale" },
  { key: "r16", label: "8es de finale" },
  { key: "qf", label: "Quarts" },
  { key: "sf", label: "Demies" },
  { key: "final", label: "Finale" },
] as const;

export interface RoundProbs {
  readonly qualify: number;
  readonly semi: number;
  readonly final: number;
  readonly title: number;
}

/** Probas d'atteindre les jalons clés (depuis la sortie MC). */
export function teamRoundProbs(output: SimulationOutput, team: TeamCode): RoundProbs {
  return {
    qualify: output.qualifyProb[team] ?? 0,
    semi: output.semiProb[team] ?? 0,
    final: output.finalProb[team] ?? 0,
    title: output.titleProb[team] ?? 0,
  };
}

export interface RoundMatch {
  readonly round: string;
  /** id du match (créneau du bracket) le PLUS probable pour l'équipe à ce tour, ou null. */
  readonly matchId: number | null;
  /** P(l'équipe occupe ce créneau) — sert à signaler un lieu encore incertain. */
  readonly prob: number;
}

/**
 * Pour chaque tour KO, le MATCH (créneau du bracket) le plus probable pour `team` —
 * d'où l'on tire le STADE et l'HORAIRE. Le créneau le plus probable = le nœud où la
 * proba d'occupation de l'équipe est maximale. nodeId « M73 » → id de match 73. Pur.
 */
export function likeliestMatchByRound(output: SimulationOutput, team: TeamCode): RoundMatch[] {
  return ROUNDS.map(({ key }) => {
    let bestNode: string | null = null;
    let bestP = 0;
    for (const nodeId of nodesByRound[key] ?? []) {
      const dist = output.slotDistribution[nodeId];
      if (!dist) continue;
      const p = (dist.a[team] ?? 0) + (dist.b[team] ?? 0);
      if (p > bestP) {
        bestP = p;
        bestNode = nodeId;
      }
    }
    const idNum = bestNode ? Number(bestNode.slice(1)) : NaN;
    return { round: key, matchId: Number.isFinite(idNum) ? idNum : null, prob: bestP };
  });
}

export interface OpponentChance {
  readonly code: TeamCode;
  readonly prob: number; // normalisé : P(adversaire | l'équipe atteint ce tour)
  readonly matchId: number | null; // créneau (match) le plus probable où l'équipe l'affronte
}

const NODE_INFO = bracketJson.nodes as unknown as Record<
  string,
  { round: string; winnerTo: [string, string] | null }
>;

/** Nœud suivant du bracket (où va le VAINQUEUR) + le côté qu'y prend l'équipe. */
export function nextBracketNode(nodeId: string): { nodeId: string; side: "a" | "b" } | null {
  const w = NODE_INFO[nodeId]?.winnerTo;
  return w ? { nodeId: w[0], side: w[1] as "a" | "b" } : null;
}

/** Reverse de winnerTo : pour chaque (nœud, côté), le nœud dont le VAINQUEUR vient l'occuper. */
const FEEDER: Record<string, { a?: string; b?: string }> = (() => {
  const out: Record<string, { a?: string; b?: string }> = {};
  for (const [id, n] of Object.entries(NODE_INFO)) {
    if (n.winnerTo) (out[n.winnerTo[0]] ??= {})[n.winnerTo[1] as "a" | "b"] = id;
  }
  return out;
})();

/**
 * Étiquettes de créneau des 16es (placements de groupe : « 1A », « 2B », « 3:ABCDF »…)
 * qui peuvent ALIMENTER le côté `side` du nœud `node`, en remontant tout le bracket.
 * PUR : ne dépend que de la structure (winnerTo) + des libellés de créneau injectés via
 * `slotOf` (donc indépendant des résultats). Ex. côté adverse du 8es d'une équipe → les
 * 1-2 créneaux R32 d'où peut venir l'adversaire ; en quart → jusqu'à 4 ; etc.
 */
export function r32SlotsFeeding(
  node: string,
  side: "a" | "b",
  slotOf: (matchId: number) => { home: string; away: string } | undefined,
): string[] {
  const feeder = FEEDER[node]?.[side];
  if (!feeder) {
    const s = slotOf(Number(node.slice(1)));
    const label = side === "a" ? s?.home : s?.away;
    return label ? [label] : [];
  }
  return [...r32SlotsFeeding(feeder, "a", slotOf), ...r32SlotsFeeding(feeder, "b", slotOf)];
}

/** Suite de NŒUDS (un par tour, 16es→finale) en suivant winnerTo depuis un nœud R32. */
export function nodePathFrom(r32Node: string): string[] {
  const path: string[] = [];
  let node: string | null = r32Node;
  while (node) {
    path.push(node);
    node = nextBracketNode(node)?.nodeId ?? null;
  }
  return path;
}

export interface PositionPath {
  /** Place finale dans le groupe (1/2/3). */
  readonly place: number;
  /** P(l'équipe occupe le créneau R32 de cette place) selon le MC. */
  readonly prob: number;
  /** Nœud R32 (le plus probable) de cette place → d'où l'on déduit le parcours/villes. */
  readonly r32Node: string;
  readonly r32Side: "a" | "b";
}

/**
 * Par PLACE de groupe atteignable (1ᵉʳ/2ᵉ/meilleur 3ᵉ), la proba MC que l'équipe y
 * finisse ET le créneau R32 correspondant — car la place fixe le créneau, donc tout le
 * PARCOURS (villes des tours suivants). Lit slotDistribution (donc suit les données) ;
 * `slotOf` injecte les libellés de créneau (« 1A »/« 2B »/« 3:… »). Pur.
 */
export function teamPositionPaths(
  output: SimulationOutput,
  team: TeamCode,
  slotOf: (matchId: number) => { home: string; away: string } | undefined,
): PositionPath[] {
  const byPlace = new Map<number, { prob: number; bestP: number; node: string; side: "a" | "b" }>();
  for (const nodeId of nodesByRound["r32"] ?? []) {
    const dist = output.slotDistribution[nodeId];
    const slots = slotOf(Number(nodeId.slice(1)));
    if (!dist || !slots) continue;
    for (const side of ["a", "b"] as const) {
      const p = dist[side]?.[team] ?? 0;
      if (p <= 0) continue;
      const c = (side === "a" ? slots.home : slots.away)[0];
      const place = c === "1" ? 1 : c === "2" ? 2 : c === "3" ? 3 : 0;
      if (!place) continue;
      const e = byPlace.get(place) ?? { prob: 0, bestP: 0, node: nodeId, side };
      e.prob += p;
      if (p > e.bestP) {
        e.bestP = p;
        e.node = nodeId;
        e.side = side;
      }
      byPlace.set(place, e);
    }
  }
  return [...byPlace.entries()]
    .map(([place, e]) => ({ place, prob: e.prob, r32Node: e.node, r32Side: e.side }))
    .sort((a, b) => a.place - b.place);
}

/** Côté (a/b) le plus probable de l'équipe à un nœud (depuis slotDistribution). */
export function teamSideAt(output: SimulationOutput, team: TeamCode, nodeId: string): "a" | "b" {
  const d = output.slotDistribution[nodeId];
  return (d?.a[team] ?? 0) >= (d?.b[team] ?? 0) ? "a" : "b";
}

/** Adversaires de l'équipe à UN nœud (côté opposé), normalisés, top N, avec matchId = ce nœud. */
export function nodeOpponents(
  output: SimulationOutput,
  team: TeamCode,
  nodeId: string,
  teamSide: "a" | "b",
  topN = 4,
): OpponentChance[] {
  const d = output.slotDistribution[nodeId];
  const other = teamSide === "a" ? "b" : "a";
  const entries = Object.entries(d?.[other] ?? {}).filter(([c]) => c !== team);
  const total = entries.reduce((a, [, p]) => a + p, 0);
  const idNum = Number(nodeId.slice(1));
  const matchId = Number.isFinite(idNum) ? idNum : null;
  return entries
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([code, p]) => ({ code, prob: total > 0 ? p / total : 0, matchId }));
}
export interface RoundOpponents {
  readonly round: string;
  readonly label: string;
  /** ≈ P(l'équipe atteint ce tour). */
  readonly reach: number;
  readonly opponents: readonly OpponentChance[];
}

const nodesByRound = ((): Record<string, string[]> => {
  const out: Record<string, string[]> = {};
  for (const [id, n] of Object.entries(NODES)) (out[n.round] ??= []).push(id);
  return out;
})();

/**
 * Adversaires possibles de `team` à chaque tour. Pour un nœud où `team` peut être
 * dans un créneau (proba pT), l'adversaire est la distribution du créneau opposé,
 * pondérée par pT (approx. d'indépendance — exacte au 1er tour, adversaires de
 * groupes différents). On garde le top `topN` par tour, normalisé.
 */
export function opponentsByRound(output: SimulationOutput, team: TeamCode, topN = 6): RoundOpponents[] {
  const out: RoundOpponents[] = [];
  for (const { key, label } of ROUNDS) {
    // par adversaire : proba cumulée + le créneau (match) qui y contribue le plus →
    // chaque adversaire porte le STADE/HORAIRE le plus probable où on le rencontre.
    const opp: Record<string, { prob: number; bestW: number; matchId: number | null }> = {};
    for (const nodeId of nodesByRound[key] ?? []) {
      const dist = output.slotDistribution[nodeId];
      if (!dist) continue;
      const idNum = Number(nodeId.slice(1));
      const matchId = Number.isFinite(idNum) ? idNum : null;
      for (const side of ["a", "b"] as const) {
        const pT = dist[side][team] ?? 0;
        if (pT <= 0) continue;
        const other = side === "a" ? "b" : "a";
        for (const [code, pOpp] of Object.entries(dist[other])) {
          if (code === team) continue;
          const w = pT * pOpp;
          const e = (opp[code] ??= { prob: 0, bestW: 0, matchId: null });
          e.prob += w;
          if (w > e.bestW) {
            e.bestW = w;
            e.matchId = matchId;
          }
        }
      }
    }
    const reach = Object.values(opp).reduce((a, e) => a + e.prob, 0);
    const opponents = Object.entries(opp)
      .sort((a, b) => b[1].prob - a[1].prob)
      .slice(0, topN)
      .map(([code, e]) => ({ code, prob: reach > 0 ? e.prob / reach : 0, matchId: e.matchId }));
    out.push({ round: key, label, reach, opponents });
  }
  return out;
}
